/* eslint-disable */
// @ts-check
import $ from 'jquery';
import 'jquery-touchswipe';
import imagesLoaded from 'imagesloaded';
import Maybe from 'crocks/Maybe';
import { throttle } from 'throttle-debounce';

import { getModuleMarkUp, getPollModuleMarkUp } from './widgets';
import { ACTIVE_AREA, getActiveArea, resetCorners } from './corner-fold';
import * as videoEvents from './video-events';
import { fireEvents, cancelAndRemoveEvents } from './events';
import * as constants from './constants';
import {
  getNextPageIndex,
  getCurrentPages,
  currentVisiblePages,
  currentVisiblePageIndexes
} from './page-helpers';
import {
  delay,
  destroyCharts,
  getChartOptions,
  getDomElement,
  getDomElements,
  getPageDetails
} from './utils';

/** @typedef {import('./@types/pagetiger').OnlineMag} OnlineMag */

const { EVENT_TYPES, MODULES, MODULE_SUB_TYPES, MODULE_TYPES, TRANSPARENT_GIF } = constants;

// @ts-ignore
imagesLoaded.makeJQueryPlugin($);

const noDrag = 'draggable="false" ondragstart="return false;"';
const noOp = () => {};

// This value needs to remain in sync with the CSS transition time
// of the divPtiContainer_zoom element to output the interactivity
// at the correct time. When support for IE11 is dropped this can
// be replaced with a custom property.
const SHOW_INTERACTIVITY_DELAY = 1000;

const SELECTORS = {
  DISABLE_FOLD: 'mod-no-fold',
  FLIP_SOUND_CLASS: 'js-flip-sound',
  INTERACTIVITY: 'ptihtml_interactivity',
  PAGE_FOLD_LEFT: 'js-page-fold-image-left',
  PAGE_FOLD_RIGHT: 'js-page-fold-image-right',
  SHRINK: 'mod-shrink'
};

/**
 * @param {number} speed
 * @returns {[number, number]}
 */
const getDelayTimings = speed => {
  if (speed < 700) {
    return [0, 300];
  } else if (speed > 1000) {
    return [300, 800];
  }

  return [150, 500];
};

/**
 * @typedef {Object} Page
 * @property {string} jpegUrl
 * @property {number} pageIndex
 */

/**
 * Page Details
 * @typedef {Object} PageDetails
 * @property {string} id - Page ID
 * @property {string} pageClass - The CSS Class
 * @property {number} pageIndex - The Page Index
 * @property {string} pageTitle - The Page Title
 */

/**
 * @param {string} attr
 * @param {string} value
 * @returns {(el: HTMLElement) => HTMLElement} a function that accepts a single element
 */
const setAttribute = (attr, value) => el => {
  el.setAttribute(attr, value);
  return el;
};

/**
 * @param {string} className
 * @returns {(el: HTMLElement) => HTMLElement} a function that accepts a single element
 */
const removeClass = className => el => {
  el.classList.remove(className);
  return el;
};

/**
 * @param {string} className
 * @returns {(el: HTMLElement) => HTMLElement} a function that accepts a single element
 */
const addClass = className => el => {
  el.classList.add(className);
  return el;
};

/**
 * @param {string} selector
 * @param {Page?} page
 * @returns {void}
 */
const setImage = (selector, page) => {
  const imageSrc = page && page.jpegUrl ? page.jpegUrl : TRANSPARENT_GIF;
  const pageIndex = page && Number.isInteger(page.pageIndex) ? `index-${page.pageIndex}` : 'blank';
  const pageNumber =
    page && Number.isInteger(page.pageIndex) ? `page ${page.pageIndex + 1}` : 'page';

  getDomElement(selector)
    .map(setAttribute('src', imageSrc))
    .map(setAttribute('data-t', `page-${pageIndex}`))
    .map(setAttribute('alt', pageNumber));
};

class PageTurnUI {
  /** @param {OnlineMag} pageTurn */
  constructor(pageTurn) {
    this.onlineMagIssue = pageTurn;
    this.turning = false;
    this.suppressSound = true;
    this.firstRender = true;
    // Used for tracking which page fold (if any) is active
    this.previousActiveArea = ACTIVE_AREA.NONE;
    this.forcePreventFolds = false;
    this.zoomElement = document.querySelector('pinch-touch');
    this._setupSwipe();
    this.resize = this.resize.bind(this);
    this._showSingle2 = this._showSingle2.bind(this);
    this._showSingle3 = this._showSingle3.bind(this);
    this._showDouble2 = this._showDouble2.bind(this);
    this._showDouble3 = this._showDouble3.bind(this);
    this._singleGoneToPage = this._singleGoneToPage.bind(this);
    this._doubleGoneToPage = this._doubleGoneToPage.bind(this);
    this._doubleStepPage = this._doubleStepPage.bind(this);
    const { flipSound } = this.onlineMagIssue.config;

    getDomElement(`#${pageTurn.containerZoomElementID}`)
      .map(removeClass('mod-placeholder'))
      .map(addClass('mod-hide'));

    if (flipSound) {
      $('body').append(`
        <audio class="${SELECTORS.FLIP_SOUND_CLASS}" style="display: none;" preload>
          <source src="https://${this.onlineMagIssue.host}/${flipSound}" type="audio/mp3">
        </audio>`);

      /** @type {HTMLAudioElement} */
      this.flipSoundElement = document.querySelector(`.${SELECTORS.FLIP_SOUND_CLASS}`);
    }

    const events = [
      { eventName: 'ended', action: videoEvents.ended(this.onlineMagIssue) },
      { eventName: 'play', action: videoEvents.play(this.onlineMagIssue) },
      { eventName: 'pause', action: videoEvents.pause(this.onlineMagIssue, this.turning) },
      { eventName: 'timeupdate', action: videoEvents.timelineUpdate(this.onlineMagIssue) },
      { eventName: 'loadedmetadata', action: videoEvents.metaDataLoaded(this.onlineMagIssue) }
    ];

    events.map(({ eventName, action }) =>
      // Prettier-ignore
      document.addEventListener(
        eventName,
        e => {
          if (e.target instanceof HTMLVideoElement) {
            action(e);
          }
        },
        true
      )
    );
  }

  /**
   * @param {MouseEvent} e
   * @returns {void}
   */
  preventCorners(e) {
    if (e.target instanceof HTMLElement && e.target.classList.contains('page-fold')) {
      resetCorners();
    }
  }

  _setupPageFoldListeners() {
    const { elementID, zoom } = this.onlineMagIssue;
    const $$ = document.querySelector.bind(document);
    const container = $$(`#${elementID}`);
    /** @type {HTMLDivElement} */
    const pageFoldTopLeft = $$('.page-fold.mod-tl');

    /** @type {HTMLDivElement} */
    const pageFoldBottomLeft = $$('.page-fold.mod-bl');

    /** @type {HTMLDivElement} */
    const pageFoldTopRight = $$('.page-fold.mod-tr');

    /** @type {HTMLDivElement} */
    const pageFoldBottomRight = $$('.page-fold.mod-br');

    /** @type {HTMLDivElement} */
    const rightSide = $$('.page.mod-right-page');

    /** @type {HTMLDivElement} */
    const rightSideBehind = $$('.page.mod-right-behind');

    /** @type {HTMLDivElement} */
    const leftSide = $$('.page.mod-left-page');

    /** @type {HTMLDivElement} */
    const leftSideBehind = zoom ? $$('.page.mod-prev-behind') : $$('.page.mod-left-behind');

    const allFolds = [pageFoldTopLeft, pageFoldBottomLeft, pageFoldTopRight, pageFoldBottomRight];
    const allSides = [
      rightSide,
      rightSideBehind,
      ...(leftSide !== null ? [leftSide, leftSideBehind] : [])
    ];
    this.previousActiveArea = ACTIVE_AREA.NONE;

    /**
     * @typedef CornerElements
     * @type {object}
     * @property {HTMLDivElement} corner
     * @property {HTMLDivElement} side
     * @property {HTMLDivElement} sideBehind
     */

    /**
     * @param {string} activeArea
     * @param {boolean} zoom
     * @returns {CornerElements}
     */
    const getActiveCornerElements = (activeArea, zoom) => {
      const [topOrBottom, leftOrRight] = activeArea.split('');
      const side = leftOrRight === 'l' && zoom === false ? leftSide : rightSide;
      const sideBehind = leftOrRight === 'l' ? leftSideBehind : rightSideBehind;

      let corner = pageFoldBottomRight;

      if (leftOrRight === 'l' && topOrBottom === 't') {
        corner = pageFoldTopLeft;
      }

      if (leftOrRight === 'l' && topOrBottom === 'b') {
        corner = pageFoldBottomLeft;
      }

      if (leftOrRight === 'r' && topOrBottom === 't') {
        corner = pageFoldTopRight;
      }

      return {
        corner,
        side,
        sideBehind
      };
    };

    this.handleMouseMoveCorners = throttle(
      300,
      /** MouseEvent */ mouseEvent => {
        const activeArea = getActiveArea(container, mouseEvent);
        const isPopupVisible = $().ptibox.isVisible() || $().ptimessage.isVisible();

        if (
          this.previousActiveArea === activeArea ||
          this.turning ||
          this.forcePreventFolds ||
          isPopupVisible
        ) {
          return;
        }

        this.previousActiveArea = activeArea;

        allFolds.map(el => el.classList.remove('mod-show'));

        if (activeArea === ACTIVE_AREA.NONE) {
          allSides.map(el => el.removeAttribute('active-corner'));
        } else {
          const { corner, side, sideBehind } = getActiveCornerElements(activeArea, zoom);
          sideBehind.setAttribute('active-corner', activeArea);
          side.setAttribute('active-corner', activeArea);
          corner.classList.add('mod-show');
        }
      }
    );

    document.body.addEventListener('click', this.preventCorners);
    document.body.addEventListener('mousemove', this.handleMouseMoveCorners);
  }

  /**
   * @param {'tl'|'bl'|'tr'|'br'} corner
   * @param {string} action
   * @param {string} jsImageSelector
   * @returns {string}
   */
  _getPageFoldMarkUp(corner, action, jsImageSelector) {
    return this.onlineMagIssue.enableCornerFolds
      ? `
      <button class="page-fold mod-${corner}" data-t="page-fold-${corner}" data-action="${action}" data-delay-time="100" aria-hidden="true" tabindex="-1">
        <div class="page-fold-div">
            <img class="page-fold-image mod-${corner} ${jsImageSelector}">
        </div>
      </button>`
      : '';
  }

  _setupSwipe() {
    const { containerElementID, containerZoomElementID, onlineMagShowPaging } = this.onlineMagIssue;

    const shouldSwipe = function ($zoomEl) {
      // Toggled inside of pinch-touch.js when zoom === 1
      if (window.disableSwipe === true) {
        return false;
      }

      const zoomEl = $zoomEl.get(0);
      // Will have to update this regex is any other transform properties get added
      const scaleRegEx = /[0-9\.]+/;

      if (zoomEl) {
        const { transform } = zoomEl.style;
        const scaleStr = transform.match(scaleRegEx);
        // Give it a default just in-case zooming isn't active on this issue
        const scaleNumber = parseFloat(scaleStr) || 1;

        return scaleNumber === 1;
      }

      return false;
    };
    /**
     *
     * @param {MouseEvent} _event
     * @param {'left'|'right'} direction
     */
    const handleSwipe = function (_event, direction) {
      const functionToCall = direction === 'right' ? window.ptiPreviousPage : window.ptiNextPage;

      if (shouldSwipe(this)) {
        window.setTimeout(() => functionToCall(containerElementID), 50);
      }
    };

    if (onlineMagShowPaging) {
      $(`#${containerZoomElementID}`).swipe({
        swipeRight: handleSwipe,
        swipeLeft: handleSwipe,
        preventDefaultEvents: false
      });
    }
  }

  _resetZoom() {
    if (this.zoomElement) {
      this.zoomElement.setAttribute('zoom', '1');
    }
  }

  previousPage() {
    let nextPage = 0;
    const { currentPageIndex, zoom } = this.onlineMagIssue;

    this._resetZoom();

    if (currentPageIndex > 0) {
      if (zoom) {
        nextPage = currentPageIndex - 1;
      } else {
        nextPage = currentPageIndex - 2;
      }

      if (nextPage < 0) {
        nextPage = 0;
      }

      if (zoom) {
        this._singleGotoPage(nextPage);
      } else {
        this._doubleGotoPage(nextPage);
      }
    }
  }

  nextPage() {
    const { currentPageIndex, config, onlineMagPages, zoom } = this.onlineMagIssue;
    var nextPageIndex = 0;

    this._resetZoom();

    if (currentPageIndex < onlineMagPages.length - 1) {
      if (zoom) {
        nextPageIndex = currentPageIndex + 1;
      } else {
        nextPageIndex = currentPageIndex + 2;
      }

      if (nextPageIndex > onlineMagPages.length - 1) {
        if (config.alwaysOpened && !zoom) {
          nextPageIndex = onlineMagPages.length - 2;
        } else {
          nextPageIndex = onlineMagPages.length - 1;
        }
      }

      if (nextPageIndex !== currentPageIndex) {
        if (zoom) {
          this._singleGotoPage(nextPageIndex);
        } else {
          this._doubleGotoPage(nextPageIndex);
        }
      }
    }
  }

  /**
   * @param {number} pageIndex
   */
  gotoPage(pageIndex) {
    this._resetZoom();

    if (pageIndex < 0 || pageIndex >= this.onlineMagIssue.onlineMagPages.length) {
      return;
    }

    if (this.onlineMagIssue.zoom) {
      this._singleGotoPage(pageIndex);
    } else {
      this._doubleGotoPage(pageIndex);
    }
  }

  /**
   * @param {number} pageIndex
   */
  gotoPageSlow(pageIndex) {
    this.gotoPage(pageIndex);
  }

  pauseVideos() {
    $(`#${this.onlineMagIssue.elementID} video`).map((_index, videoEl) => {
      try {
        if (videoEl instanceof HTMLVideoElement) {
          videoEl.pause();
        }
      } catch (ex) {
        console.warn(ex.toString());
      }
    });
  }

  /**
   * @param {string} selector
   * @param {boolean} shouldDelay
   */
  glow(selector = '', shouldDelay = true) {
    const glowClass = 'mod-glow';
    const delayMs = shouldDelay ? 300 : 0;

    selector = selector.length > 0 ? selector : '.interactivity-item';

    getDomElements(selector).map(element => {
      delay(delayMs).then(() => element.classList.add(glowClass));

      delay(900 + delayMs).then(() => element.classList.remove(glowClass));
    });
  }

  resize() {
    const { elementID, enableCornerFolds, zoom: isSinglePageMode, sizes } = this.onlineMagIssue;
    const $containerInner = $(`#${elementID}`);

    if (enableCornerFolds) {
      const { pageWidth } = isSinglePageMode ? sizes.singlePage : sizes.doublePage;
      const size = (pageWidth / 100) * 8.5;
      const fadeState = (pageWidth / 100) * 4;

      getDomElements('.page, .page-fold').map(el =>
        el.style.setProperty('--cornerSize', `${size}px`)
      );

      getDomElements('.page.mod-right-behind, .page.mod-left-behind, .page.mod-prev-behind').map(
        el => {
          el.style.setProperty('--cornerFadeStart', `${fadeState}px`);
          // Make that a percentage.
          el.style.setProperty('--cornerFadeStop', `${fadeState + 20}px`);
        }
      );
    }

    if (isSinglePageMode) {
      const { pageWidth, pageHeight } = sizes.singlePage;
      const pageWidthPx = `${pageWidth}px`;
      const pageHeightPx = `${pageHeight}px`;

      $containerInner.css({
        height: pageHeightPx,
        width: pageWidthPx
      });

      $containerInner
        .children('div')
        .not('.ptiIssueError')
        .css({ height: pageHeightPx, width: pageWidthPx });

      $containerInner.find('.page-image').css({
        height: pageHeightPx,
        width: pageWidthPx
      });

      $containerInner.find('.page.mod-prev-behind').css({
        backgroundSize: `${pageWidthPx} ${pageHeightPx}`,
        width: `${pageWidth / 2}px`
      });
    } else {
      const { pageHeight, pageWidth } = sizes.doublePage;
      const pageWidthPx = `${pageWidth}px`;
      const pageHeightPx = `${pageHeight}px`;

      $containerInner.css({
        height: `${pageHeight}px`,
        width: `${pageWidth * 2}px`
      });

      $containerInner
        .children('div')
        .not('.ptiIssueError')
        .not('.page-shadow')
        .css({ height: '100%', width: pageWidthPx });

      $containerInner.find('.mod-right-behind').css('left', pageWidthPx);
      $containerInner.find('.mod-left-page').css('right', pageWidthPx);
      $containerInner.find('.mod-right-page').css('left', pageWidthPx);
      $containerInner
        .find('.js-page-fold-image-left, .js-page-fold-image-right')
        .css({ width: pageWidthPx, height: pageHeightPx });

      $containerInner.find('.page-image').css({ height: pageHeightPx, width: pageWidthPx });
    }

    currentVisiblePages(this.onlineMagIssue)
      .map(page => this.onlineMagIssue.pages[page.pageIndex])
      .flatMap(page => page.modules.filter(mod => mod.moduleType === MODULES.CHART))
      .map(chart => {
        const chartWidth = getDomElement(`.js-chart-${chart.id}`)
          .map(el => el.getBoundingClientRect().width)
          .option(chart.width);

        chart.instance.map(instance => {
          instance.options = getChartOptions(chartWidth, chart);
          instance.update();
        });
      });
  }

  /**
   * @param {boolean} zoom
   */
  show(zoom) {
    document.body.removeEventListener('mousemove', this.handleMouseMoveCorners);
    document.body.removeEventListener('click', this.preventCorners);

    this._resetZoom();
    // Based on the current state of this.onlineMagIssue and it current configuration
    // show the UI at its currentPageIndex
    this.onlineMagIssue.zoom = zoom;
    this.onlineMagIssue.zoom ? this._showSingle() : this._showDouble();
    this.suppressSound = false;
  }

  _showDouble() {
    this._scrollTop(this._showDouble2);
  }

  _showDouble2() {
    const { containerZoomElementID } = this.onlineMagIssue;

    this.onlineMagIssue.zoom = false;
    this.onlineMagIssue.updatePageSize();
    const markup = this.onlineMagIssue.elementMarkup();

    $(`#${containerZoomElementID}`).html(markup).animate(
      {},
      {
        duration: 500,
        easing: 'swing',
        complete: this._showDouble3
      }
    );
  }

  _showDouble3() {
    this._doubleInit();
    this._doubleGotoPage(this.onlineMagIssue.currentPageIndex);
  }

  _doubleInit() {
    const { elementID, pageShadows } = this.onlineMagIssue;
    const manualPageTurnClass = this.onlineMagIssue.enableCornerFolds ? '' : 'mod-no-fold';

    $('.js-zoom').attr('page-view', 'double');

    $(`#${elementID}`).html(`
      <div class="page ${manualPageTurnClass} mod-left-behind" style="left: 0px;" data-t="page-turn" aria-hidden="true">
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%; left: 0px;" src="${TRANSPARENT_GIF}">
      </div>
      <div class="page ${manualPageTurnClass} mod-right-behind" aria-hidden="true">
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%; right: 0px;" src="${TRANSPARENT_GIF}">
      </div>

      <div class="page ${manualPageTurnClass} mod-left-page" data-t="left-page">
        ${this._getPageFoldMarkUp('tl', 'ptiPreviousPage', SELECTORS.PAGE_FOLD_LEFT)}
        ${this._getPageFoldMarkUp('bl', 'ptiPreviousPage', SELECTORS.PAGE_FOLD_LEFT)}
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%; right: 0px;" src="${TRANSPARENT_GIF}">
        <div class="${
          SELECTORS.INTERACTIVITY
        }" style="display: none;" data-t="left-page-interactivity"></div>
        </div>
      <div class="page ${manualPageTurnClass} mod-right-page" data-t="right-page">
        ${this._getPageFoldMarkUp('tr', 'ptiNextPage', SELECTORS.PAGE_FOLD_RIGHT)}
        ${this._getPageFoldMarkUp('br', 'ptiNextPage', SELECTORS.PAGE_FOLD_RIGHT)}
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%; left: 0px;" src="${TRANSPARENT_GIF}">
        <div class="${
          SELECTORS.INTERACTIVITY
        }" style="display: none;" data-t="right-page-interactivity"></div>
      </div>

      <div class="page mod-above mod-left-above" aria-hidden="true">
        <img class="page-image" ${noDrag} alt="transparent image" style="right: 0px;" src="${TRANSPARENT_GIF}">
      </div>
      <div class="page mod-above mod-right-above" aria-hidden="true">
        <img class="page-image" ${noDrag} alt="transparent image" style="left: 0px" src="${TRANSPARENT_GIF}">
      </div>

      ${pageShadows
        .map(cssClass => `<div class="page-shadow mod-${cssClass}" data-t="page-shadow"></div>`)
        .option('')}`);

    if (this.onlineMagIssue.enableCornerFolds) {
      this._setupPageFoldListeners();
    }

    this.resize();
  }

  /**
   * @param {number} pageIndex - Page Index
   * @returns {void}
   */
  _doubleGotoPage(pageIndex) {
    cancelAndRemoveEvents();

    const objUI = this;
    const objOnlineMagIssue = this.onlineMagIssue;
    const {
      currentPageIndex,
      elementID,
      config,
      isTigerPlayer,
      pageShadows,
      pageTurnSpeed,
      sizes
    } = this.onlineMagIssue;
    const { flipSound } = config;
    const { pageWidth } = sizes.doublePage;
    const $container = $(`#${elementID}`);

    if (this.turning) {
      return;
    }

    const cachedCurrentPageIndex = currentPageIndex;
    const nextPageIndex = getNextPageIndex(objOnlineMagIssue, pageIndex);

    objOnlineMagIssue.currentPageIndex = nextPageIndex;

    const [nextPageLeft, nextPageRight] = getCurrentPages(objOnlineMagIssue);
    const onAnimationComplete = () => objUI._doubleGoneToPage(nextPageLeft, nextPageRight);

    destroyCharts(this._getAllCharts());

    $container
      .find(
        `.mod-left-page .${SELECTORS.INTERACTIVITY}, .mod-right-page .${SELECTORS.INTERACTIVITY}`
      )
      .empty()
      .hide();

    // Shrink the pages behind a small amount to prevent a 1px slither showing through.
    $('.page.mod-right-behind, .page.mod-prev-behind, .page.mod-left-behind').removeClass(
      SELECTORS.SHRINK
    );
    $('.page.mod-right-behind').removeClass('mod-show');

    if (pageIndex < cachedCurrentPageIndex) {
      // Prev
      this.turning = true;

      if (flipSound) {
        this._playSound();
      }

      $container.find('.mod-left-above').css({
        left: '0',
        width: '0',
        visibility: 'visible'
      });

      $container.find('.mod-right-above').css({
        left: `${pageWidth * 2}px`,
        width: '0',
        visibility: 'hidden'
      });

      $container
        .find('.mod-left-page-behind .page-image')
        .css('visibility', nextPageLeft ? 'visible' : 'hidden');

      setImage(`.mod-left-behind .page-image`, nextPageLeft);
      setImage(`.mod-left-above .page-image`, nextPageRight);
      setImage(`.mod-right-above .page-image`, nextPageLeft);

      if (!isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $container.imagesLoaded().always(() => {
        const $page = $container.find('.mod-left-page');
        const stepperFn = this._doubleStepPage($page);

        if (!objOnlineMagIssue.isTigerPlayer) {
          $.fn.ptibox.hideLoading();
        }

        $container.find('.mod-left-above').animate(
          {
            left: `${pageWidth}px`,
            width: `${pageWidth}px`
          },
          {
            duration: pageTurnSpeed,
            step: stepperFn,
            easing: 'easeInOutQuint',
            complete: onAnimationComplete
          }
        );

        $(`.mod-left-above, .mod-left-page`).dequeue('RightPage');
      });
    } else if (pageIndex > cachedCurrentPageIndex) {
      // Next
      this.turning = true;

      if (flipSound) {
        this._playSound();
      }

      // If last page, hide the behind.
      $container.find('.mod-left-above').css({
        left: '0',
        width: '0',
        visibility: 'hidden'
      });

      $container.find('.mod-right-above').css({
        left: `${pageWidth * 2}px`,
        width: '0',
        visibility: 'visible'
      });

      $container
        .find('.mod-right-page-behind .page-image')
        .css('visibility', nextPageRight ? 'visible' : 'hidden');

      setImage(`.mod-right-behind .page-image`, nextPageRight);
      setImage(`.mod-left-above .page-image`, nextPageRight);
      setImage(`.mod-right-above .page-image`, nextPageLeft);

      if (!isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $container.imagesLoaded().always(() => {
        const $page = $container.find('.mod-right-page');
        const stepperFn = this._doubleStepPage($page);

        if (!isTigerPlayer) {
          $.fn.ptibox.hideLoading();
        }

        $container.find('.mod-right-above').animate(
          {
            left: '0',
            width: `${pageWidth}px`
          },
          {
            duration: pageTurnSpeed,
            step: stepperFn,
            easing: 'easeInOutQuint',
            complete: onAnimationComplete
          }
        );

        $(`.mod-right-above, .mod-right-page`).dequeue('LeftPage');
      });
    } else {
      objOnlineMagIssue.currentPageIndex = nextPageIndex;
      // Initial Page Render
      if (nextPageLeft) {
        setImage(`.mod-right-above .page-image`, nextPageLeft);
      }

      if (nextPageRight) {
        setImage(`.mod-left-above .page-image`, nextPageRight);
      }

      if (!isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $(`#${elementID}`)
        .imagesLoaded()
        .always(() => {
          if (!isTigerPlayer) {
            $.fn.ptibox.hideLoading();
          }

          onAnimationComplete();
        });
    }

    if (pageShadows.option('').length > 0) {
      getDomElement('.page-shadow').map(pageShadow => {
        const hiddenClass = 'mod-hide';
        const [firstDelay, secondDelay] = getDelayTimings(pageTurnSpeed);

        delay(firstDelay).then(() => pageShadow.classList.add(hiddenClass));
        delay(secondDelay).then(() => {
          if (nextPageRight !== null && nextPageLeft !== null) {
            pageShadow.classList.remove(hiddenClass);
          }
        });
      });
    }

    objOnlineMagIssue.currentPageIndex = nextPageIndex;
  }

  /**
   * @param {JQuery} $el
   */
  _doubleStepPage($el) {
    const { sizes } = this.onlineMagIssue;
    return function (now, fx) {
      if (fx.prop === 'width') {
        $el.css('width', `${sizes.doublePage.pageWidth - now}px`);
      }
    };
  }

  /**
   * Once the animation has finished, put everything in place ready for the next page turn.
   * @param {Page?} leftPage
   * @param {Page?} rightPage
   */
  _doubleGoneToPage(leftPage, rightPage) {
    const {
      containerElementID,
      containerZoomElementID,
      currentPageIndex,
      elementID,
      enableCornerFolds,
      onlineMagPages,
      sizes
    } = this.onlineMagIssue;
    const $container = $(`#${elementID}`);
    const { pageWidth } = sizes.doublePage;

    if (leftPage) {
      $container.find('.mod-left-page').css('width', `${pageWidth}px`);

      $container
        .find('.mod-left-page .page-image')
        .replaceWith($(`.mod-right-above .page-image`).clone());

      $container.find('.mod-left-page .page-image').css({ left: '', right: '0' });
      $container.find('.mod-left-behind').css('visibility', 'hidden').attr('aria-hidden', 'true');
      $container.find('.mod-left-page, .mod-left-behind').css('visibility', 'visible');
    } else {
      $container.find('.mod-left-behind').css('visibility', 'hidden').attr('aria-hidden', 'true');
      $container.find('.mod-left-page').css('visibility', 'hidden');
    }

    if (rightPage) {
      $container.find('.mod-right-page').css('width', `${pageWidth}px`);

      $container
        .find('.mod-right-page .page-image')
        .replaceWith($(`.mod-left-above .page-image`).clone());

      $container.find('.mod-right-page .page-image').css({ right: '', left: '0' });
      $container.find('.mod-right-page, .mod-right-behind').css('visibility', 'visible');
    } else {
      // If we don't have an right-hand last page, hide the right.
      $container.find('.mod-right-page, .mod-right-behind').css('visibility', 'hidden');
    }

    if (enableCornerFolds) {
      let maybeRightPageNext = rightPage && onlineMagPages.items[rightPage.pageIndex + 2];
      let maybeLeftPagePrev = leftPage && onlineMagPages.items[leftPage.pageIndex - 2];
      let maybeRightFold = rightPage && onlineMagPages.items[rightPage.pageIndex + 1];
      let maybeLeftFold = leftPage && onlineMagPages.items[leftPage.pageIndex - 1];

      // Set this so when we peel back the page we see the correct page underneath
      if (maybeRightPageNext) {
        setImage(`.mod-right-behind .page-image`, maybeRightPageNext);
      } else {
        getDomElement(`.mod-right-behind .page-image`).map(setAttribute('src', TRANSPARENT_GIF));
      }

      getDomElements(`.${SELECTORS.PAGE_FOLD_RIGHT}`).map(
        setAttribute('src', maybeRightFold ? maybeRightFold.jpegUrl : TRANSPARENT_GIF)
      );

      if (maybeLeftPagePrev) {
        setImage(`.mod-left-behind .page-image`, maybeLeftPagePrev);
      } else {
        getDomElement(`.mod-left-behind .page-image`).map(setAttribute('src', TRANSPARENT_GIF));
      }

      getDomElements(`.${SELECTORS.PAGE_FOLD_LEFT}`).map(
        setAttribute('src', maybeLeftFold ? maybeLeftFold.jpegUrl : TRANSPARENT_GIF)
      );
    }

    // Ensure the pages behind are shrink ever so slightly to prevent the back pages
    // from bleeding through.
    $('.page.mod-right-behind, .page.mod-prev-behind, .page.mod-left-behind').addClass(
      SELECTORS.SHRINK
    );

    $container.find('.mod-left-above').css({
      left: '0',
      width: '0',
      visibility: 'hidden'
    });

    $container.find('.mod-right-above').css({
      left: `${pageWidth * 2}px`,
      width: '0',
      visibility: 'hidden'
    });

    if (currentPageIndex >= 3) {
      setImage(`.mod-right-above .page-image`, onlineMagPages.items[currentPageIndex - 3]);
    }

    if (currentPageIndex <= onlineMagPages.length - 3) {
      setImage(`.mod-left-above .page-image`, onlineMagPages.items[currentPageIndex + 2]);
    }

    if (enableCornerFolds) {
      $(
        '.mod-left-page, .mod-left-page .page-fold, .mod-right-page, .mod-right-page .page-fold'
      ).removeClass(SELECTORS.DISABLE_FOLD);

      // Disable the left side page corners if we're on the first page
      if (leftPage && leftPage.pageIndex === 0) {
        $('.mod-left-page, .mod-left-page .page-fold').addClass(SELECTORS.DISABLE_FOLD);
      }

      // Disable the right side page corners if we're on the last page
      if (rightPage && rightPage.pageIndex === onlineMagPages.length - 1) {
        $('.mod-right-page, .mod-right-page .page-fold').addClass(SELECTORS.DISABLE_FOLD);
      }

      $('.page.mod-right-behind').addClass('mod-show');

      this.previousActiveArea = ACTIVE_AREA.NONE;
    }

    this.turning = false;
    this.forcePreventFolds = false;

    /** @type {Page[]} */
    const pages = [];

    /** @type {any[]} */
    const chainData = [];

    if (leftPage) {
      pages.push(leftPage);
      chainData.push(...leftPage.chain);
    }

    if (rightPage) {
      pages.push(rightPage);
      chainData.push(...rightPage.chain);
    }

    if (!this.onlineMagIssue.suppressChainOnPageShow) {
      fireEvents(EVENT_TYPES.ON_SHOW, chainData);
    }

    window.ptiPagesViewed(containerElementID, leftPage, rightPage);

    this._preloadImages();

    if (this.firstRender) {
      delay(500).then(() => {
        $(`#${containerZoomElementID}`).removeClass('mod-hide');

        delay(SHOW_INTERACTIVITY_DELAY).then(() => {
          // It's possible that the user has quickly changes page before the transitionend fires.
          // This checks that the page index which was initial set still matches the pages currently
          // displayed
          const matchingIndexes = pages.filter(page =>
            currentVisiblePageIndexes(this.onlineMagIssue).includes(page.pageIndex)
          );

          if (matchingIndexes.length > 0) {
            this._outputInteractivity(pages);
          }
        });
      });
    } else {
      this._outputInteractivity(pages);
    }

    this.onlineMagIssue.suppressChainOnPageShow = false;
    this.firstRender = false;
  }

  /**
   * @param {function} cb
   */
  _scrollTop(cb) {
    if (document.documentElement.scrollTop > 0) {
      $('html').animate({ scrollTop: 0, duration: 500, easing: 'swing', complete: cb });
    } else {
      cb();
    }
  }

  _showSingle() {
    this._scrollTop(this._showSingle2);
  }

  _showSingle2() {
    var objUI = this;
    var objOnlineMagIssue = objUI.onlineMagIssue;
    objOnlineMagIssue.zoom = true;
    objOnlineMagIssue.updatePageSize();

    $(`#${objOnlineMagIssue.containerZoomElementID}`)
      .html(objOnlineMagIssue.elementMarkup())
      .animate(
        {},
        {
          duration: 500,
          easing: 'swing',
          complete: objUI._showSingle3
        }
      );
  }

  _showSingle3() {
    this._singleInit();
    this._singleGotoPage(this.onlineMagIssue.currentPageIndex);
  }

  _singleInit() {
    const { elementID } = this.onlineMagIssue;
    const $containerInner = $(`#${elementID}`);
    const singlePageClass = this.onlineMagIssue.enableCornerFolds ? 'mod-single' : '';
    const manualPageTurnClass = this.onlineMagIssue.enableCornerFolds ? '' : 'mod-no-fold';

    $('.js-zoom').attr('page-view', 'single');
    $containerInner.html(`
      <div class="page mod-prev-behind"></div>

      <div class="page mod-right-behind" data-t="page-turn">
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%; right: 0px;" src="${TRANSPARENT_GIF}">
      </div>

      <div class="page mod-right-page ${singlePageClass} ${manualPageTurnClass}" data-t="right-page" data-t="page">
        ${this._getPageFoldMarkUp('tl', 'ptiPreviousPage', SELECTORS.PAGE_FOLD_LEFT)}
        ${this._getPageFoldMarkUp('bl', 'ptiPreviousPage', SELECTORS.PAGE_FOLD_LEFT)}
        ${this._getPageFoldMarkUp('tr', 'ptiNextPage', SELECTORS.PAGE_FOLD_RIGHT)}
        ${this._getPageFoldMarkUp('br', 'ptiNextPage', SELECTORS.PAGE_FOLD_RIGHT)}
        <img class="page-image" ${noDrag} alt="transparent image" style="width: 100%;  left: 0px;" src="${TRANSPARENT_GIF}">
        <div class="${
          SELECTORS.INTERACTIVITY
        }" style="display: none;" data-t="right-page-interactivity"></div>
      </div>

      <div class="page mod-above mod-grey-bg mod-left-above">
        <img class="page-image" ${noDrag} alt="transparent image" style="right: 0px;" src="${TRANSPARENT_GIF}">
      </div>

      <div class="page mod-above mod-grey-bg mod-right-above">
        <img class="page-image" ${noDrag} alt="transparent image" style="left: 0px;" src="${TRANSPARENT_GIF}">
      </div>

      <div class="page mod-grey-bg mod-current-page" style="display: none; position: absolute;">
        <img class="page-image" ${noDrag} alt="transparent image" style="left: 0px;" src="${TRANSPARENT_GIF}">
      </div>
    `);

    if (this.onlineMagIssue.enableCornerFolds) {
      this._setupPageFoldListeners();
    }

    this.resize();
  }

  _playSound() {
    const soundDelay = this.onlineMagIssue.pageTurnSpeed > 1000 ? 400 : 0;

    if (this.flipSoundElement && !this.suppressSound) {
      delay(soundDelay).then(() => {
        const playPromise = this.flipSoundElement.play();

        // IE11 doesn't return a promise
        if (playPromise) {
          playPromise.then(noOp).catch(noOp);
        }
      });
    }

    this.suppressSound = false;
  }

  _getAllCharts() {
    return this.onlineMagIssue.pages.reduce(
      (acc, page) => [...acc, ...page.modules.filter(mod => mod.moduleType === 11)],
      []
    );
  }

  /**
   * @param {number} pageIndex
   */
  _singleGotoPage(pageIndex) {
    cancelAndRemoveEvents();

    const objUI = this;
    const objOnlineMagIssue = this.onlineMagIssue;
    const { currentPageIndex, elementID, onlineMagPages, sizes } = this.onlineMagIssue;
    const nextPage = onlineMagPages.items[pageIndex];
    const { flipSound } = objOnlineMagIssue.config;
    const { pageWidth } = sizes.singlePage;
    const pageWidthPx = `${pageWidth}px`;
    const complete = () => this._singleGoneToPage(nextPage);
    const queue = pageIndex < currentPageIndex ? 'LeftPageSingle' : 'RightPageSingle';
    const baseAnimationProperties = {
      duration: objOnlineMagIssue.pageTurnSpeed,
      easing: 'easeInOutQuint',
      queue
    };
    const $container = $(`#${elementID}`);

    $('.page.mod-prev-behind').css({ opacity: '0' });

    if (this.turning) {
      return;
    }

    destroyCharts(this._getAllCharts());

    $(`.mod-right-page .${SELECTORS.INTERACTIVITY}`).empty().hide();
    $('.page.mod-right-behind, .page.mod-prev-behind, .page.mod-left-behind').removeClass(
      SELECTORS.SHRINK
    );

    if (pageIndex < currentPageIndex) {
      // Prev
      objUI.turning = true;

      if (flipSound) {
        this._playSound();
      }

      setImage('.mod-current-page .page-image', nextPage);

      $container.find('.mod-right-page').css({ left: '', right: 0 });
      $container.find('.mod-right-page .page-image').css({ left: '', right: 0 });

      setImage('.mod-right-behind .page-image', nextPage);

      $container.find('.mod-left-above').css({
        left: `-${pageWidthPx}`,
        width: `${pageWidthPx}`,
        visibility: 'visible'
      });

      setImage(`.mod-left-above .page-image`, null);

      $container.find('.mod-right-above').css('visibility', 'hidden').attr('aria-hidden', 'true');

      setImage(`.mod-right-above .page-image`, nextPage);

      if (!objOnlineMagIssue.isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $container.imagesLoaded().always(() => {
        if (!objOnlineMagIssue.isTigerPlayer) {
          $.fn.ptibox.hideLoading();
        }

        // If ever want to switch away from jQuery animation to the native browser animation
        // this is a good starting point.

        // import 'web-animations-js' <-- needed for IE11
        // const animationProperties = { easing: 'cubic-bezier(0.860, 0.000, 0.070, 1.000)', duration: 800 };
        // const singlePageTurn = $container.find('.mod-left-above').get(0).animate([{
        //   left: 0,
        //   width: `${$container.find('.mod-left-above').width()}px`
        // }, {
        //   left: pageWidthPx,
        //   width: '0'
        // }], animationProperties);

        // $container.find('.mod-right-page').get(0).animate([{
        //   width: `${$container.find('.mod-right-page').width()}px`
        // }, {
        //   width: '0'
        // }], animationProperties);

        // singlePageTurn.onfinish = complete;

        $container
          .find('.mod-left-above')
          .animate({ left: pageWidthPx, width: '0' }, { ...baseAnimationProperties, complete });

        $container.find('.mod-right-page').animate({ width: '0' }, baseAnimationProperties);

        $('.mod-left-above, .mod-right-page').dequeue('LeftPageSingle');
      });
    } else if (pageIndex > currentPageIndex) {
      // Next
      objUI.turning = true;

      if (flipSound) {
        this._playSound();
      }

      setImage(`.mod-current-page .page-image`, nextPage);

      $container.find('.mod-right-page').css({ right: '', left: '0' });
      $container.find('.mod-right-page .page-image').css({ right: '', left: '0' });

      setImage(`.mod-right-behind .page-image`, nextPage);

      $container.find('.mod-left-above').css('visibility', 'hidden').attr('aria-hidden', 'true');

      setImage(`.mod-left-above .page-image`, nextPage);

      $container
        .find('.mod-right-above')
        .css({ left: pageWidthPx, width: '0', visibility: 'visible' });

      setImage('.mod-right-above .page-image', null);

      if (!objOnlineMagIssue.isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $container.imagesLoaded().always(() => {
        if (!objOnlineMagIssue.isTigerPlayer) {
          $.fn.ptibox.hideLoading();
        }

        $container
          .find('.mod-right-above')
          .animate(
            { left: `-${pageWidthPx}`, width: pageWidthPx },
            { ...baseAnimationProperties, complete }
          );

        $container.find('.mod-right-page').animate({ width: '0' }, baseAnimationProperties);

        $('.mod-right-above, .mod-right-page').dequeue('RightPageSingle');
      });
    } else {
      setImage('.mod-current-page .page-image', nextPage);

      if (!objOnlineMagIssue.isTigerPlayer) {
        $.fn.ptibox.showLoading();
      }

      $container.imagesLoaded().always(() => {
        if (!objOnlineMagIssue.isTigerPlayer) {
          $.fn.ptibox.hideLoading();
        }

        objUI._singleGoneToPage(nextPage);
      });
    }

    objOnlineMagIssue.currentPageIndex = pageIndex;
  }

  /**
   * Once the animation has finished, put everything in place ready for the next page turn.
   * @param {Page} page
   */
  _singleGoneToPage(page) {
    const {
      containerElementID,
      containerZoomElementID,
      currentPageIndex,
      enableCornerFolds,
      onlineMagPages,
      sizes
    } = this.onlineMagIssue;

    /** @type Page[] */
    const pages = [page];
    const { pageWidth } = sizes.singlePage;

    if (!this.onlineMagIssue.suppressChainOnPageShow) {
      fireEvents(EVENT_TYPES.ON_SHOW, page.chain);
    }

    $(`.mod-right-page`).css({
      width: `${pageWidth}px`,
      right: '',
      left: '0'
    });

    $(`.mod-right-page .page-image`).replaceWith($(`.mod-current-page .page-image`).clone());

    $(`.mod-right-page .page-image`).css({
      right: '',
      left: 0
    });

    $(`.mod-right-page`).css('visibility', 'visible');

    $(`.mod-left-above`)
      .css({
        left: '0',
        width: 0,
        visibility: 'hidden'
      })
      .attr('aria-hidden', 'true');

    $(`.mod-right-above`)
      .css({
        left: `${pageWidth}px`,
        width: '0',
        visibility: 'hidden'
      })
      .attr('aria-hidden', 'true');

    if (enableCornerFolds) {
      let maybePageNext = onlineMagPages.items[currentPageIndex + 1];
      let maybePagePrev = onlineMagPages.items[currentPageIndex - 1];

      // Set this so when we peel back the page we see the correct page underneath
      if (maybePageNext) {
        setImage(`.mod-right-behind .page-image`, maybePageNext);
      } else {
        getDomElement(`.mod-right-behind .page-image`).map(setAttribute('src', TRANSPARENT_GIF));
      }

      if (maybePagePrev) {
        $('.page.mod-prev-behind').css('background-image', `url(${maybePagePrev.jpegUrl}`);
      } else {
        getDomElement(`.mod-left-behind .page-image`).map(setAttribute('src', TRANSPARENT_GIF));
      }

      $('.page.mod-right-page.mod-single')
        .removeClass(`${SELECTORS.DISABLE_FOLD}-left`)
        .removeClass(`${SELECTORS.DISABLE_FOLD}-right`);

      $('.page.mod-right-page.mod-single .page-fold').removeClass(SELECTORS.DISABLE_FOLD);

      // Disable the left side corners if we're on the first page.
      if (currentPageIndex === 0) {
        $('.page.mod-right-page.mod-single').addClass(`${SELECTORS.DISABLE_FOLD}-left`);
        $(
          '.page.mod-right-page.mod-single .page-fold.mod-tl, .page.mod-right-page.mod-single .page-fold.mod-bl'
        ).addClass(SELECTORS.DISABLE_FOLD);
      }

      // Disable the right side corners if we're on the last page.
      if (currentPageIndex === onlineMagPages.length - 1) {
        $('.page.mod-right-page.mod-single').addClass(`${SELECTORS.DISABLE_FOLD}-right`);
        $(
          '.page.mod-right-page.mod-single .page-fold.mod-tr, .page.mod-right-page.mod-single .page-fold.mod-br'
        ).addClass(SELECTORS.DISABLE_FOLD);
      }

      $('.page.mod-prev-behind').css({ opacity: '1' });

      this.previousActiveArea = ACTIVE_AREA.NONE;
    }

    $('.page.mod-right-behind, .page.mod-prev-behind, .page.mod-left-behind').addClass(
      SELECTORS.SHRINK
    );

    this.turning = false;
    this.forcePreventFolds = false;

    window.ptiPagesViewed(containerElementID, null, page);

    if (this.firstRender) {
      delay(500).then(() => {
        $(`#${containerZoomElementID}`).removeClass('mod-hide');

        delay(SHOW_INTERACTIVITY_DELAY).then(() => {
          // It's possible that the user has quickly changes page before the transitionend fires.
          // This checks that the page index which was initial set still matches the pages currently
          // displayed
          const matchingIndexes = pages.filter(page =>
            currentVisiblePageIndexes(this.onlineMagIssue).includes(page.pageIndex)
          );

          if (matchingIndexes.length > 0) {
            this._outputInteractivity(pages);
          }
        });
      });
    } else {
      this._outputInteractivity(pages);
    }

    this.firstRender = false;
    this._preloadImages();
    this.onlineMagIssue.suppressChainOnPageShow = false;
  }

  /**
   * @param {boolean} alwaysOpened
   * @param {boolean} zoom
   * @param {number} pageIndex
   * @returns {string}
   */
  _getPageClass(alwaysOpened, zoom, pageIndex) {
    if (zoom) {
      return 'mod-right-page';
    }

    const oddOrEven = alwaysOpened ? 1 : 0;

    return pageIndex % 2 === oddOrEven ? 'mod-right-page' : 'mod-left-page';
  }

  /**
   * @param {Page[]} visiblePages - PageDetails
   * @returns {void}
   */
  _outputInteractivity(visiblePages) {
    const {
      pages,
      config,
      onlineMagClickableAreaStyle,

      zoom
    } = this.onlineMagIssue;
    const pageWidth = getPageDetails(zoom, 'pageWidth');
    const pageHeight = getPageDetails(zoom, 'pageHeight');

    visiblePages.map(page => {
      const pageClass = this._getPageClass(config.alwaysOpened, zoom, page.pageIndex);
      const $interactivityEl = $(`.${pageClass} .${SELECTORS.INTERACTIVITY}`);
      const modules = pages[page.pageIndex].modules;

      const moduleMarkUp = modules
        .filter(mod => mod.moduleType !== MODULES.POLL_ANSWER)
        .map(mod =>
          getModuleMarkUp(mod, pageHeight, pageWidth, page.pageIndex, onlineMagClickableAreaStyle)
        )
        .join('');

      // This isn't the most efficient way we could group poll answers
      // together within pages. That's a reasonably big refactor.
      const pollMarkup = getPollModuleMarkUp(
        modules.filter(mod => mod.moduleType === MODULES.POLL_ANSWER),
        pageHeight,
        pageWidth
      );

      $interactivityEl.append(moduleMarkUp);
      $interactivityEl.append(pollMarkup);
    });

    visiblePages.map(page => {
      pages[page.pageIndex].modules
        .filter(mod => mod.moduleType === MODULES.CHART && mod.subType === MODULE_SUB_TYPES.CHART)
        .map(chartModule => {
          getDomElement(`.js-chart-${chartModule.id}`).map(
            /** @param {HTMLCanvasElement} chartElement */ chartElement => {
              import(/* webpackChunkName: "chart" */ 'chart.js')
                .then(({ default: Chart }) => {
                  const chartWidth = chartElement.getBoundingClientRect().width;
                  const options = getChartOptions(chartWidth, chartModule);

                  chartModule.instance = Maybe.Just(
                    new Chart(chartElement.getContext('2d'), { ...chartModule.chartData, options })
                  );
                  chartModule.isComplete = true;

                  chartElement.innerHTML = chartModule.chartTableHtml;
                })
                .catch(err => {
                  window.errorReporter.sendError({
                    col: 0,
                    line: 0,
                    message: err.message,
                    stack: err.stack,
                    type: 'TypeError'
                  });
                });
            }
          );

          // Set the in-page charts to complete as soon as they are rendered
          if (chartModule.subType === MODULE_SUB_TYPES.CHART) {
            const pageTurn = window.ptiGetInstance();
            chartModule.isComplete = true;
            pageTurn.onModuleComplete(chartModule);
          }
        });
    });

    $('.' + SELECTORS.INTERACTIVITY).fadeIn();

    if (onlineMagClickableAreaStyle === 'hover') {
      this.glow(undefined, false);
    }
  }

  /**
   * @param {string} imageUrl - Image url to preload
   * @returns {void}
   */
  _preLoadImage(imageUrl) {
    const image = new Image();
    image.src = imageUrl;
  }

  /**
   * @returns {void}
   */
  _preloadImages() {
    const { currentPageIndex, onlineMagPages } = this.onlineMagIssue;
    const { cacheSize } = this.onlineMagIssue.config;

    Array(cacheSize)
      .fill(0)
      .map((_item, index) => {
        if (currentPageIndex - index > -1) {
          this._preLoadImage(onlineMagPages.items[currentPageIndex - index].jpegUrl);
        }

        if (currentPageIndex + index < onlineMagPages.items.length) {
          this._preLoadImage(onlineMagPages.items[currentPageIndex + index].jpegUrl);
        }
      });
  }
}

export default PageTurnUI;
